import { useEffect } from "react";
import { InverntroyService } from "../../../services/api/inventroy.service";
import { useAuth } from "../../../context/AuthContext";
import { useState } from "react";
import { Product } from "../Admin/InventoryManagement/types";
import { AdminOrderService } from "../../../services/api/AdminOrderService/admin.order.service";
import medicineIcon from "../../../assets/images/medicineIcon.png";
import InvoiceHistory from "../Admin/InventoryManagement/InvoiceHistory";
import ExpiryAlert from "../Admin/InventoryManagement/ExpiryAlert";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import RequestedStock from "./RequestedStock";
import ProductEnquiryTab from "./ProductEnquiryTab";

const InventoryDetails = ({
  activeInventoryTab,
  inventoryData,
  onTabChange,
}: any) => {
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [invoiceHistory, setInvoiceHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";
  const [downloading, setDownloading] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await AdminOrderService.getAdminProductListing(
          currentPage,
          10,
          searchQuery,
          clinicId,
        );

        const transformedProducts = response.data.map((item: any) => ({
          product_id: item.product_id || item.id,
          product_name: item.product?.name || "Unknown Product",
          manufacturer_name:
            item.manufacturer?.manufacturer_name || "Unknown Manufacturer",
          product_image: item.product?.productImage || medicineIcon,
          landing_cost: parseFloat(item.landing_cost) || 0,
          mrp: parseFloat(item.mrp) || 0,
          quantity: item.no_of_items || 0,
          expiry_date: item.expiry_date,
          batch_number: item.batch_number,
          hsn_number: item.hsn_number,
          product_rate: parseFloat(item.product_rate) || 0,
          discount: parseFloat(item.discount) || 0,
          finalSellingPrice:
            parseFloat(item.productPrice?.final_selling_price) || null,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
          free_items: item.free_items || 0,
          remainingQuantity:
            item.remainingQuantity > 0
              ? item.remainingQuantity
              : item.remaining_stocks,
          tabletPerStrip: item.product.tablet_per_stripe,
        }));

        console.log("Transformed Products:", transformedProducts);

        setAvailableProducts(transformedProducts);
        setTotalPages(response.pagination.totalPages);
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load products",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentPage, searchQuery]);

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";

      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };

  const formatExpiryDate = (dateString: string | undefined): string => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";

      return date.toLocaleDateString("en-IN", {
        month: "short", // e.g., Jan, Feb, Mar
        year: "numeric", // e.g., 2025
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };

  useEffect(() => {
    const fetchInvoiceHistory = async () => {
      try {
        const response = await InverntroyService.getInvoiceHistoryForClinic(
          1,
          10,
          clinicId,
        );
        console.log("inventory", response);
        setInvoiceHistory(response.data);
      } catch (error) {
        console.error("Failed to fetch invoice history:", error);
      }
    };
    if (activeInventoryTab === "invoice-history") {
      fetchInvoiceHistory();
    }
  }, [activeInventoryTab, inventoryData?.clinicId]);

  const handleExportToExcel = async () => {
    setDownloading(true);
    try {
      const BATCH_PAGE_SIZE = 200;

      // Step 1: Fetch first page to get total pages count
      const firstPageResponse = await AdminOrderService.getAdminProductListing(
        1,
        BATCH_PAGE_SIZE,
        searchQuery,
        clinicId,
      );

      if (!firstPageResponse.data || firstPageResponse.data.length === 0) {
        toast.error("No data found");
        return;
      }

      const totalBatchPages = firstPageResponse.pagination.totalPages;
      let allData: any[] = [...firstPageResponse.data];

      // Step 2: Fetch remaining pages in parallel batches of 5
      if (totalBatchPages > 1) {
        toast.loading(`Downloading data... (1/${totalBatchPages} pages)`, { id: "export-progress" });

        const CONCURRENT_LIMIT = 5;
        for (let startPage = 2; startPage <= totalBatchPages; startPage += CONCURRENT_LIMIT) {
          const endPage = Math.min(startPage + CONCURRENT_LIMIT - 1, totalBatchPages);
          const pagePromises: Promise<any>[] = [];

          for (let page = startPage; page <= endPage; page++) {
            pagePromises.push(
              AdminOrderService.getAdminProductListing(
                page,
                BATCH_PAGE_SIZE,
                searchQuery,
                clinicId,
              )
            );
          }

          const results = await Promise.all(pagePromises);
          results.forEach((res) => {
            if (res.data && res.data.length > 0) {
              allData = [...allData, ...res.data];
            }
          });

          toast.loading(`Downloading data... (${Math.min(endPage, totalBatchPages)}/${totalBatchPages} pages)`, { id: "export-progress" });
        }

        toast.dismiss("export-progress");
      }

      // Step 3: Convert the accumulated data into Excel-friendly structure
      const formattedData = allData.map((item: any) => {
        let expiryFormatted = "N/A";

        if (item.expiry_date) {
          const date = new Date(item.expiry_date);
          expiryFormatted = date.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          });
        }

        const tabletPreStrip = item.product.tablet_per_stripe;
        const quantity =
          item.remainingQuantity > 0
            ? item.remainingQuantity
            : item.remaining_stocks;

        const remainingQuantity =
          tabletPreStrip > 1
            ? `Strips: ${Math.floor(
              quantity / tabletPreStrip,
            )} Tablets: ${quantity % tabletPreStrip}`
            : `${quantity} Pieces`;

        return {
          "Product Name": item.product?.name || "N/A",
          "Batch Number": item?.batch_number || "N/A",
          "Invoice Number": item?.invoice_number || "N/A",
          "Invoice Date": item?.invoice_date
            ? new Date(item.invoice_date).toLocaleDateString("en-US")
            : "N/A",
          Rate: Number(item?.product_rate || 0).toFixed(2) || "N/A",
          MRP: Number(item?.mrp || 0).toFixed(2) || "N/A",
          "Selling Price":
            parseFloat(item.productPrice?.final_selling_price) || "N/A",
          "Expiry Date": expiryFormatted,
          "Remaining Quantity": remainingQuantity,
        };
      });

      // Create a worksheet
      const worksheet = XLSX.utils.json_to_sheet(formattedData);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoice Details");

      // Generate Excel File
      XLSX.writeFile(workbook, `invoice_details.xlsx`);
      toast.success(`Exported ${allData.length} records successfully!`);
    } catch (error) {
      console.log(error);
      toast.dismiss("export-progress");
      toast.error("Error while exporting the invoice details");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200  -mt-5  duration-200  ">
      <div className="flex border-b justify-between items-center pl-2 border-gray-200 mb-4 bg-gray-50 rounded-t-lg">
        <div className="flex items-center space-x-2 px-4">
          <div
            onClick={() => onTabChange("inventory-list")}
            className={`px-6 py-3 cursor-pointer border-b-2 transition-all duration-200 hover:bg-gray-100 ${activeInventoryTab === "inventory-list"
                ? "border-green-500 text-green-600 font-semibold"
                : "border-transparent text-gray-500"
              }`}
          >
            <span className="font-medium">Inventory list</span>
          </div>
          <div
            onClick={() => onTabChange("invoice-history")}
            className={`px-6 py-3 cursor-pointer border-b-2 transition-all duration-200 hover:bg-gray-100 ${activeInventoryTab === "invoice-history"
                ? "border-green-500 text-green-600 font-semibold"
                : "border-transparent text-gray-500"
              }`}
          >
            <span className="font-medium">Invoice History</span>
          </div>
          <div
            onClick={() => onTabChange("expiry-alert")}
            className={`px-6 py-3 cursor-pointer border-b-2 transition-all duration-200 hover:bg-gray-100 ${activeInventoryTab === "expiry-alert"
                ? "border-green-500 text-green-600 font-semibold"
                : "border-transparent text-gray-500"
              }`}
          >
            <span className="font-medium">Expiry Alert</span>
          </div>
          <div
            onClick={() => onTabChange("stock-request")}
            className={`px-6 py-3 cursor-pointer border-b-2 transition-all duration-200 hover:bg-gray-100 ${activeInventoryTab === "stock-request"
                ? "border-green-500 text-green-600 font-semibold"
                : "border-transparent text-gray-500"
              }`}
          >
            <span className="font-medium">Stock Request</span>
          </div>
          <div
            onClick={() => onTabChange("product-enquiry")}
            className={`px-6 py-3 cursor-pointer border-b-2 transition-all duration-200 hover:bg-gray-100 ${activeInventoryTab === "product-enquiry"
                ? "border-green-500 text-green-600 font-semibold"
                : "border-transparent text-gray-500"
              }`}
          >
            <span className="font-medium">Product Enquiry</span>
          </div>
        </div>
      </div>

      <div className="relative flex flex-col ">
        <div className="flex-1  overflow-x-auto overflow-y-auto h-full scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {activeInventoryTab === "inventory-list" && (
            // Inventory List Tab Content
            <>
              <div className="flex flex-row w-full min-w-[600px] justify-between">
                <div className="m-2 w-1/4">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div className="flex items-end justify-end m-2">
                  <button
                    onClick={handleExportToExcel}
                    className="mb-4 px-4 py-2 left-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
                    disabled={downloading}
                  >
                    {downloading ? "Exporting..." : "Export to Excel"}
                  </button>
                </div>
              </div>

              {availableProducts && availableProducts.length > 0 ? (
                <>
                  <table className="w-full min-w-[600px] table-auto">
                    <thead className="border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
                      <tr className="text-left">
                        <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                          S.No.
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                          Product Name
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                          Remaining Quantity
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                          Rate
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                          MRP
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                          Selling Price
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                          Expiry Date
                        </th>
                        <th className="px-4 py-3 text-sm font-semibold text-gray-600">
                          Batch Number
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {availableProducts.map((product: any, index: number) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-500 font-medium">
                            {(currentPage - 1) * 10 + index + 1}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 flex items-center">
                            <img
                              src={product?.product_image}
                              alt={product?.product_name}
                              className="w-10 h-10 rounded-lg mr-3 object-cover border border-gray-200"
                              onError={(e) => {
                                e.currentTarget.src =
                                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMjAgMTJDMTguODk1NCAxMiAxOCAxMi44OTU0IDE4IDE0QzE4IDE1LjEwNDYgMTguODk1NCAxNiAyMCAxNkMyMS4xMDQ2IDE2IDIyIDE1LjEwNDYgMjIgMTRDMjIgMTIuODk1NCAyMS4xMDQ2IDEyIDIwIDEyWiIgZmlsbD0iI0Q0RDRENCIvPgo8cGF0aCBkPSJNMTIgMjBDMTIgMTYuNjg2MyAxNC42ODYzIDE0IDE4IDE0SDIyQzI1LjMxMzcgMTQgMjggMTYuNjg2MyAyOCAyMFYyNEMyOCAyNy4zMTM3IDI1LjMxMzcgMzAgMjIgMzBIMThDMTQuNjg2MyAzMCAxMiAyNy4zMTM3IDEyIDI0VjIwWiIgZmlsbD0iI0Q0RDRENCIvPgo8L3N2Zz4K";
                              }}
                            />
                            {product?.product_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {product?.tabletPerStrip > 1
                              ? `Strips: ${Math.floor(
                                product?.remainingQuantity /
                                product?.tabletPerStrip,
                              )} Tablets: ${product?.remainingQuantity %
                              product?.tabletPerStrip
                              }`
                              : `${product?.remainingQuantity} Pieces`}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            ₹{product?.product_rate}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            ₹{product?.mrp}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {product.finalSellingPrice
                              ? `₹${product.finalSellingPrice}`
                              : "Not Updated"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatExpiryDate(product?.expiry_date)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {product?.batch_number}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="sticky bottom-0 right-0 flex justify-end items-center py-3 px-4 bg-white border-t">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="p-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      <span className="px-4 py-2 text-sm text-gray-700 font-medium">
                        Page {currentPage} of {totalPages}
                      </span>
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-8 h-8 flex items-center justify-center rounded ${currentPage === pageNum
                                  ? "bg-green-500 text-white"
                                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                                } transition-colors duration-200`}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="p-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 5l7 7-7 7M5 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No products found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchQuery
                      ? "No products match your search criteria."
                      : "No products available in inventory."}
                  </p>
                  {searchQuery && (
                    <button
                      onClick={() =>
                        handleSearchChange({ target: { value: "" } } as any)
                      }
                      className="mt-4 px-4 py-2 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors duration-200"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              )}
            </>
          )}
          {activeInventoryTab === "invoice-history" && (
            <div className="w-full overflow-hidden">
              <InvoiceHistory
                searchTerm=""
                showInvoiceDetails={showInvoiceDetails}
                setShowInvoiceDetails={setShowInvoiceDetails}
                clinicId={clinicId}
              />
            </div>
          )}
          {activeInventoryTab === "expiry-alert" && (
            <div className="w-full overflow-hidden">
              <ExpiryAlert clinicId={clinicId} />
            </div>
          )}
          {activeInventoryTab === "stock-request" && (
            <div className="w-full overflow-hidden">
              <RequestedStock clinicId={clinicId} />
            </div>
          )}
          {activeInventoryTab === "product-enquiry" && (
            <div className="w-full overflow-hidden">
              <ProductEnquiryTab clinicId={clinicId} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export function ClinicInventoryManagement() {
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";
  const [invoiceHistory, setInvoiceHistory] = useState<any[]>([]);
  const [activeInventoryTab, setActiveInventoryTab] = useState<
    "inventory-list" | "invoice-history" | "expiry-alert"
  >("inventory-list");
  const [inventoryData, setInventoryData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const fetchInvoiceHistory = async () => {
      try {
        setLoading(true);
        const response = await InverntroyService.getInvoiceHistoryForClinic(
          1,
          10,
          clinicId,
        );
        console.log("inventory", response);
        setInvoiceHistory(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch invoice history:", error);
      }
    };
    if (activeInventoryTab === "invoice-history") {
      fetchInvoiceHistory();
    }
  }, [activeInventoryTab]);
  const handleInventoryTabChange = (
    tab: "inventory-list" | "invoice-history" | "expiry-alert",
  ) => {
    setActiveInventoryTab(tab);
  };
  return (
    <div>
      <h1>Clinic Inventory Management</h1>
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
          </div>
        )}
        <InventoryDetails
          activeInventoryTab={activeInventoryTab}
          inventoryData={inventoryData}
          onTabChange={handleInventoryTabChange}
        />
      </div>
    </div>
  );
}